// Loan dashboard — role-aware quick actions. One compact row; each action
// is HIDDEN (never disabled-greyed) when the caller lacks the same gate the
// target surface applies. Payments / Insurance auto-open their existing
// modals via the tab's ?action= param; Reporting / QA are plain deep links
// (their flows are series-card / mutation driven — no modal to auto-open).

import { Link } from "react-router-dom";
import { ClipboardList, CreditCard, FileText, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface QuickAction {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  allowed: boolean;
}

export function QuickActionsRow({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const isAdmin = auth.hasRoleCode("ADMIN");
  const base = `/portfolio-admin/loans/${loanId}`;

  const actions: QuickAction[] = [
    {
      key: "enter-payment",
      label: "Enter payment",
      icon: CreditCard,
      to: `${base}?tab=payment-tracking&action=record-payment`,
      allowed: auth.hasPermission("Payments.Create") || isAdmin,
    },
    {
      key: "record-insurance",
      label: "Record insurance certificate",
      icon: ShieldCheck,
      to: `${base}?tab=insurance&action=add-policy`,
      allowed: auth.hasPermission("Insurance.Verify") || isAdmin,
    },
    {
      key: "create-report",
      label: "Create report",
      icon: FileText,
      to: `${base}?tab=financial-ops`,
      allowed: auth.hasPermission("Loans.Manage") || isAdmin,
    },
    {
      key: "start-qa-review",
      label: "Start QA review",
      icon: ClipboardList,
      to: `${base}?tab=legal-ops`,
      allowed: auth.hasPermission("Compliance.QaReview") || isAdmin,
    },
  ].filter((a) => a.allowed);

  if (actions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-space-2" data-testid="loan-quick-actions">
      {actions.map((a) => (
        <Link
          key={a.key}
          to={a.to}
          className="inline-flex items-center gap-space-1 rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx hover:bg-white-warm no-underline"
          data-testid={`quick-action-${a.key}`}
        >
          <a.icon size={14} aria-hidden="true" />
          {a.label}
        </Link>
      ))}
    </div>
  );
}
